import type { BillingOrdwayChargeModel, BillingOrdwayPlanModel } from '@customerio/hydra-ui-api';
import { BillingSystem } from '../types';
import type { BillingPlan } from '../types';
import { OrdwayBillingPeriod } from '../ordway/plans';
import { ProductName } from '../ordway/products';
import {
  findBaseOrdwayItem,
  findOrdwayItemByProductName,
  OrdwayChargeType,
  ordwayHighlights,
  ordwayIntervalDisplayName,
  parseOrdwayFeatureGroups,
} from '../ordway/utils';
import { PlanType } from '../plans';

/** Typed view of the loose `custom_fields` bag on an Ordway plan. */
interface OrdwayPlanCustomFields {
  cancellable?: boolean;
  /** A free-form wire string (e.g. `"essentials"`, `"pause"`, `"trial"`, or `""`), not a validated enum. */
  plan_type?: string;
  feature_groups?: string;
  is_free?: boolean;
}

/** Typed view of the loose `custom_fields` bag on an Ordway charge. */
interface OrdwayChargeCustomFields {
  /** A free-form wire string, see {@link OrdwayPlanCustomFields.plan_type}. */
  plan_type?: string;
}

/**
 * An Ordway plan transformed into the shared {@link BillingPlan} shape, with the raw
 * wire fields preserved side-by-side (mirroring the Ember model instance).
 */
export type OrdwayPlanBillingPlan = BillingOrdwayPlanModel &
  BillingPlan & { billingSystem: typeof BillingSystem.Ordway };

export function planCustomFields(plan: BillingOrdwayPlanModel): OrdwayPlanCustomFields {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return (plan.custom_fields ?? {}) as OrdwayPlanCustomFields;
}

function chargeCustomFields(charge: BillingOrdwayChargeModel): OrdwayChargeCustomFields {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return (charge.custom_fields ?? {}) as OrdwayChargeCustomFields;
}

/**
 * A plan's tier is tagged on the *plan's own* `custom_fields.plan_type` — confirmed against the real
 * Ordway wire payload, where every charge's `custom_fields` comes back empty and `plan_type` only ever
 * appears at the plan level (e.g. `"essentials"`, `"pause"`, `"trial"`, or `""` for plans with no
 * tier, like internal/legacy catalog entries). Falls back to a per-charge tag for any catalog shape
 * that does carry it there.
 *
 * `plan_type` is a free-form wire string (like the Stripe transform's `plan.plan_type`), not a
 * validated enum, so a present tag is cast to `PlanType` rather than checked against the known union —
 * `plan.planType === PlanType.Essentials` comparisons downstream only need non-Essentials plans (like
 * "pause") to compare unequal, not every wire value to map to a modeled tier.
 *
 * When neither the plan nor any charge names a tier at all, this must NOT default to
 * `PlanType.Essentials`: that was the bug (SPE-3860 investigation) — real Ordway catalogs include
 * untagged internal plans (e.g. "CIO Employees", a disabled "DNU Essentials") ahead of the real
 * Essentials plan in `getPlans()`, so `essentialsPlan`'s `.find(plan => plan.planType ===
 * PlanType.Essentials)` picked whichever untagged plan came first. `NotReportable` is the existing
 * "doesn't map to a real tier" bucket, so it can never collide with a genuine `Essentials` match.
 */
function planType(plan: BillingOrdwayPlanModel): PlanType {
  const planLevelType = planCustomFields(plan).plan_type;
  if (planLevelType != null && planLevelType !== '') {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return planLevelType as PlanType;
  }

  const charge = plan.charges?.find((candidate) => {
    let { plan_type } = chargeCustomFields(candidate);
    return plan_type != null && plan_type !== '';
  });
  const chargeLevelType = charge != null ? chargeCustomFields(charge).plan_type : undefined;
  if (chargeLevelType != null) {
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    return chargeLevelType as PlanType;
  }

  return PlanType.NotReportable;
}

function basePrice(charges: BillingOrdwayChargeModel[]): number {
  const recurringCharges = charges.filter((charge) => charge.type !== OrdwayChargeType.Usage);

  return recurringCharges.reduce((accumulator, charge) => {
    const cents = Number.parseFloat(charge.list_price);
    return cents * 100 + accumulator;
  }, 0);
}

function includedUnits(charge: BillingOrdwayChargeModel | undefined): number {
  return Number.parseInt(charge?.included_units ?? '0', 10);
}

/** Transform an Ordway plan wire model into the shared {@link BillingPlan} shape. */
// fallow-ignore-next-line complexity -- ported from Ember billing, complexity is inherent to the domain mapping
export function ordwayPlanToBillingPlan(plan: BillingOrdwayPlanModel): OrdwayPlanBillingPlan {
  const charges = plan.charges ?? [];
  const base = findBaseOrdwayItem(charges);

  const customersCharge = findOrdwayItemByProductName(charges, ProductName.JourneysCustomersOverages);
  const messagesCharge = findOrdwayItemByProductName(charges, ProductName.JourneysMessageOverages);
  const objectsCharge = findOrdwayItemByProductName(charges, ProductName.JourneysObjectsOverages);
  const profilesCharge = findOrdwayItemByProductName(charges, ProductName.JourneysProfilesOverages);
  const mauCharge = findOrdwayItemByProductName(charges, ProductName.MonthlyActiveUsers);

  const type = planType(plan);
  const groups = parseOrdwayFeatureGroups(planCustomFields(plan).feature_groups);

  const includedProfiles = includedUnits(profilesCharge);
  const includedMessages = includedUnits(messagesCharge);
  const includedMau = includedUnits(mauCharge);
  const mauOverageRate = mauCharge?.list_price ?? '';
  const price = basePrice(charges);

  return {
    ...plan,
    billingSystem: BillingSystem.Ordway,
    basePrice: price,
    intervalDisplayName: ordwayIntervalDisplayName(base?.billing_period),
    includedCustomers: includedUnits(customersCharge),
    includedMessages,
    includedObjects: includedUnits(objectsCharge),
    includedProfiles,
    includedMau,
    customerOverageRate: customersCharge?.list_price ?? '',
    messageOverageRate: messagesCharge?.list_price ?? '',
    objectOverageRate: objectsCharge?.list_price ?? '',
    profileOverageRate: profilesCharge?.list_price ?? '',
    mauOverageRate,
    canCancel: planCustomFields(plan).cancellable ?? false,
    planType: type,
    featureGroups: groups,
    name: plan.name,
    description: plan.description,
    isPaidAnnually: base?.billing_period === OrdwayBillingPeriod.Annually,
    annualPrice: price,
    highlights: ordwayHighlights(type, groups, includedProfiles, includedMessages),
    isTrial: planCustomFields(plan).is_free ?? false,
    isMauPricing: includedMau !== 0 && mauOverageRate !== '',
  };
}
